import { useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Key, Eye, EyeOff, Check, X } from "lucide-react";
import { API, portalHeaders } from "../helpers.js";

export default function PasswordChangeModal({ onClose, primaryColor }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const checks = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0,
    different: newPassword !== oldPassword && newPassword.length > 0,
  };
  const allValid = Object.values(checks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/portal/change-password`, {
        old_password: oldPassword, new_password: newPassword, confirm_password: confirmPassword,
      }, { headers: portalHeaders() });
      if (res.data.access_token) localStorage.setItem("portalToken", res.data.access_token);
      toast.success("Mot de passe modifie avec succes");
      onClose();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erreur");
    } finally { setLoading(false); }
  };

  const CI = ({ ok, label }) => (
    <div className={`flex items-center gap-2 text-xs ${ok ? "text-green-600" : "text-gray-400"}`}>
      {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {label}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="password-change-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" style={{ color: primaryColor }} /> Changer mon mot de passe
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actuel</label>
            <div className="relative">
              <input type={showOld ? "text" : "password"} value={oldPassword} onChange={e => setOldPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm pr-10" data-testid="modal-old-password" />
              <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-2.5 top-2.5 text-gray-400">
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau</label>
            <div className="relative">
              <input type={showNew ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm pr-10" data-testid="modal-new-password" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-2.5 top-2.5 text-gray-400">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="modal-confirm-password" />
          </div>
          <div className="bg-gray-50 rounded-lg p-2 grid grid-cols-2 gap-1">
            <CI ok={checks.length} label="8+ car." />
            <CI ok={checks.upper} label="MAJ" />
            <CI ok={checks.lower} label="min" />
            <CI ok={checks.digit} label="123" />
            <CI ok={checks.different} label="Different" />
            <CI ok={checks.match} label="Identique" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={!allValid || loading}
              className="flex-1 px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50"
              style={{ backgroundColor: primaryColor }} data-testid="modal-change-submit">
              {loading ? "..." : "Confirmer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
